/**
 * Wave F4-C — Admin > Financial Repair Tools
 *
 * A minimal, admin-only screen that operates on:
 *   GET  /api/admin/cash-reconciliation/analyze?period=YYYY-MM
 *   GET  /api/admin/cash-reconciliation/analyze.csv?period=YYYY-MM
 *   POST /api/admin/cash-reconciliation/backfill  { period, dry_run, row_ids? }
 *
 * The Backfill button is **disabled until at least one dry-run has
 * succeeded for the current period** — the same guarantee the user
 * signed off on: no writes without a diagnostic first.
 */
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldAlert, Search, PlayCircle, CheckCircle2, Download,
  AlertTriangle, XCircle, Loader2, FileWarning, Activity, Wallet,
} from 'lucide-react';
import { axiosInstance } from '../App';

const REASON_META = {
  MISSING_JOURNAL:    { cls: 'bg-rose-100 text-rose-700 border-rose-300',
                        icon: XCircle,
                        label: 'Missing Journal' },
  ORPHAN_JOURNAL:     { cls: 'bg-amber-100 text-amber-700 border-amber-300',
                        icon: FileWarning,
                        label: 'Orphan Journal' },
  UNBALANCED_POSTING: { cls: 'bg-rose-100 text-rose-700 border-rose-300',
                        icon: AlertTriangle,
                        label: 'Unbalanced' },
  BROKEN_REFERENCE:   { cls: 'bg-amber-100 text-amber-700 border-amber-300',
                        icon: AlertTriangle,
                        label: 'Broken Ref' },
};

const fmt = (n) => (n == null ? '—' :
  Number(n).toLocaleString(undefined, { minimumFractionDigits: 2,
                                          maximumFractionDigits: 2 }));

function ReasonBadge({ reason }) {
  const m = REASON_META[reason] || {
    cls: 'bg-slate-100 text-slate-600 border-slate-300',
    icon: AlertTriangle, label: reason || '—' };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 border rounded-full
                       px-2 py-0.5 text-[10px] font-bold ${m.cls}`}>
      <Icon size={10} /> {m.label}
    </span>
  );
}

export default function AdminFinancialRepair() {
  const now = new Date();
  const defaultPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1)
                                                    .padStart(2, '0')}`;

  const [period, setPeriod]         = useState(defaultPeriod);
  const [companyId, setCompanyId]   = useState('');
  const [scan, setScan]             = useState(null);
  const [breakdown, setBreakdown]   = useState(null);
  const [rootCause, setRootCause]   = useState(null);
  const [dryReport, setDryReport]   = useState(null);
  const [commitReport, setCommit]   = useState(null);
  const [loading, setLoading]       = useState(false);
  const [busy, setBusy]             = useState(false);
  const [openTrace, setOpenTrace]   = useState(null);   // idx of expanded trace

  const periodValid = /^\d{4}-(0[1-9]|1[0-2])$/.test(period);

  // Guard: /backfill commit is only enabled once a dry-run for the
  // current period has been surveyed by the operator.
  const canCommit = !!(dryReport && dryReport.params?.period === period
                      && dryReport.params?.dry_run === true);

  const runAnalyze = async () => {
    if (!periodValid) { toast.error('Enter a valid period (YYYY-MM).'); return; }
    setLoading(true); setDryReport(null); setCommit(null); setBreakdown(null); setRootCause(null);
    const cq = companyId ? `&company_id=${encodeURIComponent(companyId)}` : '';
    try {
      const r = await axiosInstance.get(
        `/admin/cash-reconciliation/analyze?period=${period}${cq}`);
      setScan(r.data);
      try {
        const b = await axiosInstance.get(
          `/admin/cash-reconciliation/variance-breakdown?period=${period}${cq}`);
        setBreakdown(b.data);
      } catch (be) { console.warn('variance-breakdown failed', be); }
      try {
        const rc = await axiosInstance.get(
          `/admin/cash-reconciliation/root-cause?period=${period}${cq}`);
        setRootCause(rc.data);
      } catch (rce) { console.warn('root-cause failed', rce); }
    } catch (e) {
      toast.error(`Analyze failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  };

  const runDry = async () => {
    setBusy(true); setCommit(null);
    try {
      const r = await axiosInstance.post(
        `/admin/cash-reconciliation/backfill`,
        { period, dry_run: true });
      setDryReport(r.data);
      toast.success(`Dry-run OK — ${r.data.totals.candidates} candidate(s)`);
    } catch (e) {
      toast.error(`Dry-run failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const runCommit = async () => {
    if (!canCommit) {
      toast.error('Run a Dry-Run first for this period.');
      return;
    }
    if (!window.confirm(
      `Commit backfill for ${period}? This posts real journals to the GL. `
      + `Idempotent — safe to re-run, but audited.`)) return;
    setBusy(true);
    try {
      const r = await axiosInstance.post(
        `/admin/cash-reconciliation/backfill`,
        { period, dry_run: false });
      setCommit(r.data);
      toast.success(`Committed — ${r.data.totals.posted} journal(s) posted`);
      // Refresh diagnostic so the operator sees the new state.
      await runAnalyze();
    } catch (e) {
      toast.error(`Commit failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const exportCsv = () => {
    if (!periodValid) return;
    const url = axiosInstance.defaults.baseURL
      + `/admin/cash-reconciliation/analyze.csv?period=${period}`;
    const token = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.download = `cash_recon_${period}.csv`;
        a.click();
      })
      .catch(e => toast.error(`Export failed: ${e.message}`));
  };

  const summary = scan?.summary;
  const rows    = scan?.rows || [];

  const rowsByReason = useMemo(() => {
    const g = {};
    for (const r of rows) (g[r.reason] = g[r.reason] || []).push(r);
    return g;
  }, [rows]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5"
          data-testid="admin-financial-repair-page">
      <header className="flex items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex
                          items-center gap-2">
            <ShieldAlert className="text-rose-600" size={26} />
            Financial Repair Tools
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Row-level forensic diagnostic for the Cash ↔ Payments variance.
            Diagnose first — never blind-backfill. The Commit button
            unlocks only after a dry-run for the same period.
          </p>
          <Link
            to="/diagnostics/cash-gl-mismatch"
            className="inline-flex items-center gap-1 mt-2 text-xs
                        text-indigo-700 hover:underline"
            data-testid="link-cash-gl-console"
          >
            → افتح Cash ↔ Payments Diagnostic Console (تقرير + إصلاح سريع)
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="repair-company-input"
            type="text"
            value={companyId}
            onChange={e => setCompanyId(e.target.value)}
            placeholder="company_id (blank = mine)"
            className="border border-slate-300 rounded px-3 py-1.5 text-sm
                        font-mono w-56"
          />
          <input
            data-testid="repair-period-input"
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="2026-01"
            className="border border-slate-300 rounded px-3 py-1.5 text-sm
                        font-mono w-28"
          />
          <button
            data-testid="repair-analyze-btn"
            onClick={runAnalyze}
            disabled={loading || !periodValid}
            className="inline-flex items-center gap-2 bg-indigo-600
                        hover:bg-indigo-700 text-white rounded px-4 py-1.5
                        text-sm font-semibold disabled:opacity-50">
            {loading
              ? <Loader2 size={14} className="animate-spin" />
              : <Search size={14} />}
            Analyze
          </button>
          <button
            data-testid="repair-export-btn"
            onClick={exportCsv}
            disabled={!scan}
            className="inline-flex items-center gap-2 bg-slate-100
                        hover:bg-slate-200 text-slate-800 rounded px-3 py-1.5
                        text-sm font-semibold disabled:opacity-40">
            <Download size={14} /> CSV
          </button>
        </div>
      </header>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3"
              data-testid="repair-kpis">
          {[
            ['Total Rows',     summary.total_rows,          'text-slate-900'],
            ['Missing Journal', summary.total_missing,     'text-rose-700'],
            ['Orphan Journal', summary.total_orphans,       'text-amber-700'],
            ['Unbalanced',     summary.total_unbalanced,    'text-rose-700'],
            ['Broken Ref',     summary.total_broken_ref,    'text-amber-700'],
            ['Total Amount (SAR)', fmt(summary.total_amount), 'text-slate-900'],
          ].map(([k, v, cls]) => (
            <div key={k} className="bg-white border border-slate-200 rounded-lg
                                     p-3">
              <div className="text-[10px] uppercase text-slate-500 font-semibold">
                {k}
              </div>
              <div className={`text-xl font-bold mt-1 ${cls}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {summary && summary.total_rows === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg
                         p-4 flex items-center gap-3"
              data-testid="repair-clean-banner">
          <CheckCircle2 className="text-emerald-600" size={22} />
          <div>
            <div className="font-bold text-emerald-800">
              No discrepancies found for {period}
            </div>
            <div className="text-sm text-emerald-700">
              Every approved payment and paid voucher in this period has
              a matching, balanced GL journal.
            </div>
          </div>
        </div>
      )}

      {/* ── Variance Breakdown — explains Cash ↔ Payments variance ─── */}
      {breakdown && Math.abs(breakdown.variance) > 0.5 && (
        <div className="bg-white border-2 border-amber-300 rounded-lg
                         overflow-hidden" data-testid="repair-variance-panel">
          <div className="bg-amber-50 px-4 py-3 border-b border-amber-200
                          flex items-center gap-2">
            <Activity className="text-amber-700" size={18} />
            <div>
              <div className="font-bold text-amber-900">
                Cash ↔ Payments Variance Breakdown
              </div>
              <div className="text-xs text-amber-700">
                Matches the exact calculation of{' '}
                <code>reconciliation_engine._check_cash</code>. Explains
                why a variance exists even when the primary Analyze
                reports 0 rows.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4
                          border-b border-slate-100 text-xs">
            <div>
              <div className="text-slate-500 uppercase font-semibold">
                Expected (Sub-Ledger)
              </div>
              <div className="text-lg font-bold font-mono text-slate-900">
                {fmt(breakdown.expected)}
              </div>
            </div>
            <div>
              <div className="text-slate-500 uppercase font-semibold">
                Actual (GL)
              </div>
              <div className="text-lg font-bold font-mono text-slate-900">
                {fmt(breakdown.actual)}
              </div>
            </div>
            <div>
              <div className="text-slate-500 uppercase font-semibold">
                Variance
              </div>
              <div className="text-lg font-bold font-mono text-amber-700">
                {(breakdown.variance > 0 ? '+' : '') + fmt(breakdown.variance)}
              </div>
            </div>
            <div>
              <div className="text-slate-500 uppercase font-semibold">
                Classification Issues
              </div>
              <div className="text-lg font-bold font-mono text-indigo-700">
                {breakdown.summary.root_classification_issues ?? 0}
              </div>
            </div>
            <div>
              <div className="text-slate-500 uppercase font-semibold">
                Posting Issues
              </div>
              <div className="text-lg font-bold font-mono text-rose-700">
                {breakdown.summary.root_posting_issues ?? 0}
              </div>
            </div>
          </div>

          {/* CTA — jump straight to CoA Classification when the root
              cause is a metadata issue */}
          {(breakdown.summary.root_classification_issues ?? 0) > 0 && (
            <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-200
                            text-xs text-indigo-900 flex items-center gap-2">
              <Wallet size={14} />
              <div className="flex-1">
                <b>{breakdown.summary.root_classification_issues}</b>{' '}
                payment(s) hit an account that <i>looks like</i> cash
                but isn't flagged <code>is_cash_account</code>. Fix
                the flag on those accounts — no journal edits needed.
              </div>
              <Link to="/admin/coa-classification"
                    data-testid="repair-open-coa-btn"
                    className="inline-flex items-center gap-1 bg-indigo-600
                               hover:bg-indigo-700 text-white rounded
                               px-2 py-1 font-semibold">
                Open CoA Classification →
              </Link>
            </div>
          )}

          {breakdown.payment_traces?.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase
                                text-slate-600">
                  <th className="px-3 py-2">Payment</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">Sub-Ledger Δ</th>
                  <th className="px-3 py-2">Journal</th>
                  <th className="px-3 py-2 text-right">GL Cash Δ</th>
                  <th className="px-3 py-2 text-right">Delta</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">Root Cause</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.payment_traces.map((t, i) => {
                  const ok = t.reason === 'OK';
                  const rowCls = ok
                    ? 'text-slate-500'
                    : 'text-slate-900 bg-rose-50';
                  const isOpen = openTrace === i;
                  const canExpand = t.cash_lines?.length > 0;
                  return (
                    <React.Fragment key={i}>
                      <tr
                        className={`border-t border-slate-100 ${rowCls}
                                     ${canExpand ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                        onClick={() => canExpand
                          && setOpenTrace(isOpen ? null : i)}
                        data-testid={`variance-trace-${i}`}>
                        <td className="px-3 py-2 font-semibold">
                          {canExpand && (isOpen ? '▼ ' : '▶ ')}
                          {t.payment_no}
                        </td>
                        <td className="px-3 py-2">{t.payment_date}</td>
                        <td className="px-3 py-2">{t.payment_type}</td>
                        <td className="px-3 py-2 text-right font-mono">
                          {fmt(t.amount)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          {fmt(t.sub_ledger_impact)}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {t.journal_no || '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          {fmt(t.gl_cash_impact)}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono
                                          font-bold ${ok
                            ? 'text-emerald-700'
                            : 'text-rose-700'}`}>
                          {(t.delta > 0 ? '+' : '') + fmt(t.delta)}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <span className={`px-2 py-0.5 rounded-full border
                                            font-bold ${ok
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                              : 'bg-rose-100 text-rose-700 border-rose-300'}`}>
                            {t.reason}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {t.root_cause === 'CLASSIFICATION_ISSUE' && (
                            <span className="px-2 py-0.5 rounded-full border
                                              font-bold bg-indigo-100
                                              text-indigo-700 border-indigo-300"
                                  title="Fix by ticking is_cash_account on the offending account">
                              CLASSIFICATION ✅
                            </span>
                          )}
                          {t.root_cause === 'POSTING_ISSUE' && (
                            <span className="px-2 py-0.5 rounded-full border
                                              font-bold bg-rose-200
                                              text-rose-800 border-rose-400"
                                  title="Journal touched the wrong account entirely — needs a correction entry">
                              POSTING 🔴
                            </span>
                          )}
                          {!t.root_cause && ok && (
                            <span className="text-emerald-600 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                      {isOpen && canExpand && (
                        <tr className="bg-slate-50 border-t border-rose-200"
                            data-testid={`variance-trace-detail-${i}`}>
                          <td colSpan={10} className="px-6 py-3">
                            <div className="text-[10px] uppercase font-bold
                                            text-slate-600 mb-2">
                              Journal Lines — {t.journal_no}
                              {' '}(the account column below is what your
                              journal actually touched)
                            </div>
                            <table className="w-full text-xs bg-white
                                              border border-slate-200">
                              <thead>
                                <tr className="bg-slate-100 text-left">
                                  <th className="px-2 py-1">Account #</th>
                                  <th className="px-2 py-1">System Code</th>
                                  <th className="px-2 py-1">Name</th>
                                  <th className="px-2 py-1 text-right">Debit</th>
                                  <th className="px-2 py-1 text-right">Credit</th>
                                  <th className="px-2 py-1">Classified As Cash?</th>
                                </tr>
                              </thead>
                              <tbody>
                                {t.cash_lines.map((ln, k) => (
                                  <tr key={k} className="border-t border-slate-100">
                                    <td className="px-2 py-1 font-mono">
                                      {ln.account_number || '—'}
                                    </td>
                                    <td className="px-2 py-1 font-mono">
                                      {ln.account_code || '—'}
                                    </td>
                                    <td className="px-2 py-1">
                                      {ln.account_name || '—'}
                                    </td>
                                    <td className="px-2 py-1 text-right font-mono">
                                      {fmt(ln.debit)}
                                    </td>
                                    <td className="px-2 py-1 text-right font-mono">
                                      {fmt(ln.credit)}
                                    </td>
                                    <td className="px-2 py-1">
                                      <span className={`px-2 py-0.5 rounded
                                                          text-[10px] font-bold
                                                          ${ln.is_cash_by_engine
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : 'bg-slate-200 text-slate-700'}`}>
                                        {ln.is_cash_by_engine ? 'YES ✅' : 'NO ❌'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="text-[10px] text-slate-600 mt-2
                                            italic">
                              Fix — set <code>system_code = &quot;BANK&quot;</code>
                              {' '}on the credit-side account above, OR change
                              its <code>account_number</code> to start with
                              {' '}<code>1110</code> / <code>1120</code>.
                              The Reconciliation Engine classifies cash
                              accounts by these two attributes only.
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}

          {breakdown.orphan_cash_lines?.length > 0 && (
            <div className="border-t border-slate-200">
              <div className="bg-slate-50 px-4 py-2 text-xs uppercase
                              font-semibold text-slate-600">
                Orphan Cash Lines (touch cash but have no payment link)
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-500">
                    <th className="px-3 py-2">Journal</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Account</th>
                    <th className="px-3 py-2 text-right">Debit</th>
                    <th className="px-3 py-2 text-right">Credit</th>
                    <th className="px-3 py-2">Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.orphan_cash_lines.map((l, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs">
                        {l.journal_no}
                      </td>
                      <td className="px-3 py-2">{l.journal_date}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {l.account_number} · {l.account_code}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {fmt(l.debit)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {fmt(l.credit)}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {l.reference_type ? `${l.reference_type}:${(l.reference_id || '').slice(0, 8)}…` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Wave F4-G — Root-Cause Report ─────────────────────────── */}
      {rootCause && <RootCausePanel data={rootCause} />}

      {rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg
                         overflow-hidden" data-testid="repair-rows-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase
                              text-slate-600">
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Number</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2 text-right">Amount (SAR)</th>
                <th className="px-3 py-2">Party</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}
                    className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`repair-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.source}</td>
                  <td className="px-3 py-2 font-semibold">{r.number}</td>
                  <td className="px-3 py-2">{r.date}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {fmt(r.amount)}
                  </td>
                  <td className="px-3 py-2">{r.party}</td>
                  <td className="px-3 py-2">{r.status}</td>
                  <td className="px-3 py-2"><ReasonBadge reason={r.reason} /></td>
                  <td className="px-3 py-2 text-slate-600 text-xs max-w-md">
                    {r.detail}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4
                         space-y-3" data-testid="repair-actions">
          <div className="text-sm font-bold text-slate-900">
            Actions
          </div>
          <div className="flex gap-3">
            <button
              data-testid="repair-dryrun-btn"
              onClick={runDry}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-amber-500
                          hover:bg-amber-600 text-white rounded px-4 py-2
                          text-sm font-semibold disabled:opacity-50">
              {busy
                ? <Loader2 size={14} className="animate-spin" />
                : <PlayCircle size={14} />}
              Run Dry-Run
            </button>
            <button
              data-testid="repair-commit-btn"
              onClick={runCommit}
              disabled={!canCommit || busy}
              title={canCommit
                ? 'Commit posts real journals — audited.'
                : 'Run a Dry-Run for this period first.'}
              className="inline-flex items-center gap-2 bg-rose-600
                          hover:bg-rose-700 text-white rounded px-4 py-2
                          text-sm font-semibold disabled:opacity-40">
              <CheckCircle2 size={14} />
              Commit Backfill
            </button>
          </div>

          {dryReport && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3"
                  data-testid="repair-dry-report">
              <div className="font-bold text-amber-900 mb-2">
                Dry-Run Report ({dryReport.params?.period})
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div>Candidates: <b>{dryReport.totals.candidates}</b></div>
                <div>Would-post: <b>{dryReport.totals.posted}</b></div>
                <div>DR total: <b>{fmt(dryReport.totals.dr_total)}</b></div>
                <div>Δ: <b>{fmt(dryReport.totals.delta)}</b></div>
              </div>
              {dryReport.errored?.length > 0 && (
                <div className="text-xs text-rose-700 mt-2">
                  {dryReport.errored.length} row(s) would error — inspect
                  the report before committing.
                </div>
              )}
            </div>
          )}

          {commitReport && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3"
                  data-testid="repair-commit-report">
              <div className="font-bold text-emerald-900 mb-2">
                Commit Report — Run ID <code>{commitReport.id}</code>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div>Posted: <b>{commitReport.totals.posted}</b></div>
                <div>Errored: <b>{commitReport.totals.errored}</b></div>
                <div>DR total: <b>{fmt(commitReport.totals.dr_total)}</b></div>
                <div>CR total: <b>{fmt(commitReport.totals.cr_total)}</b></div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── Wave F4-G — Root-Cause Report Panel ───────────────────────────
// Deterministic per-payment diagnostic. Never suggests a fix until
// EVERY branch (bank_account link + journal cash-side + registry
// membership) has been proven.
const VERDICT_META = {
  OK_MATCHES:              { color: 'emerald', label: 'مطابق ✓', icon: CheckCircle2 },
  JOURNAL_MISSING:         { color: 'rose',    label: 'قيد مفقود', icon: XCircle },
  JOURNAL_HITS_NO_ACCOUNT: { color: 'rose',    label: 'قيد بلا جانب نقدي', icon: XCircle },
  PAYMENT_NO_BANK:         { color: 'amber',   label: 'الدفعة بلا bank_account_id', icon: AlertTriangle },
  BANK_ACCOUNT_MISSING:    { color: 'rose',    label: 'سِجل البنك محذوف', icon: XCircle },
  BANK_NOT_LINKED_TO_GL:   { color: 'amber',   label: 'البنك غير مربوط بـ GL', icon: AlertTriangle },
  JOURNAL_HITS_UNLINKED:   { color: 'rose',    label: 'القيد على حساب مختلف', icon: XCircle },
  JOURNAL_HITS_OTHER_BANK: { color: 'amber',   label: 'القيد لبنك آخر', icon: AlertTriangle },
  JOURNAL_HITS_FLAGGED_ONLY:{ color: 'amber',  label: 'تصنيف Cache قديم',   icon: AlertTriangle },
};

function VerdictBadge({ verdict }) {
  const m = VERDICT_META[verdict] || { color: 'slate', label: verdict, icon: AlertTriangle };
  const Icon = m.icon;
  const cls = {
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    rose:    'bg-rose-100 text-rose-800 border-rose-300',
    amber:   'bg-amber-100 text-amber-800 border-amber-300',
    slate:   'bg-slate-100 text-slate-800 border-slate-300',
  }[m.color];
  return (
    <span className={`inline-flex items-center gap-1 border rounded-full
                      px-2 py-0.5 text-[11px] font-bold ${cls}`}
          data-testid={`verdict-${verdict}`}>
      <Icon size={12} /> {m.label}
    </span>
  );
}

function RootCausePanel({ data }) {
  const rows = data.rows || [];
  const banks = data.registry?.bank_accounts || [];
  const summary = data.summary_by_verdict || {};
  const nonOk = rows.filter(r => r.verdict !== 'OK_MATCHES');

  return (
    <div className="bg-white border-2 border-indigo-300 rounded-lg overflow-hidden"
         data-testid="root-cause-panel">
      <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200
                      flex items-center gap-2">
        <Wallet className="text-indigo-700" size={18} />
        <div className="flex-1">
          <div className="font-bold text-indigo-900">
            تقرير Root-Cause — لكل دفعة، لماذا التسوية غير مطابقة؟
          </div>
          <div className="text-xs text-indigo-700">
            يقارن بدقة: (bank_accounts.linked_account_id) ↔ (journal cash-side line) ↔ (Cash Registry).
            لا اقتراح إصلاح إلا بعد إثبات السبب.
          </div>
        </div>
      </div>

      {/* Registry snapshot */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4
                      border-b border-slate-100 text-xs bg-slate-50/60">
        <div>
          <div className="text-slate-500 uppercase font-semibold">حسابات Cash في Registry</div>
          <div className="text-lg font-bold text-slate-900">{data.registry?.cash_ids_count ?? 0}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase font-semibold">Bank-linked</div>
          <div className="text-lg font-bold text-slate-900">{data.registry?.bank_linked_ids_count ?? 0}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase font-semibold">مُعلَّم فقط (is_cash_account)</div>
          <div className="text-lg font-bold text-slate-900">{data.registry?.flagged_only_ids?.length ?? 0}</div>
        </div>
      </div>

      {/* Bank accounts table */}
      {banks.length > 0 && (
        <div className="border-b border-slate-100">
          <div className="bg-slate-50 px-4 py-2 text-[11px] uppercase font-semibold text-slate-600">
            Bank Accounts في هذه الشركة — حالة ربطها بـ GL
          </div>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Bank</th>
                <th className="px-3 py-2 text-left">Linked GL Account</th>
                <th className="px-3 py-2 text-left">Number</th>
                <th className="px-3 py-2 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {banks.map(b => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-semibold">{b.name}</td>
                  <td className="px-3 py-2">{b.linked_account_name || <span className="text-rose-600 font-bold">— غير مربوط —</span>}</td>
                  <td className="px-3 py-2 font-mono text-xs">{b.linked_account_number || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    {b.linked_account_id
                      ? <span className="text-emerald-700 font-bold text-xs">✓ Linked</span>
                      : <span className="text-rose-700 font-bold text-xs">✕ Missing Link</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary chips */}
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap gap-2 items-center">
        <span className="text-[11px] uppercase text-slate-500 font-semibold ms-1">
          Summary — {rows.length} payment(s):
        </span>
        {Object.entries(summary).map(([v, n]) => (
          <span key={v} className="inline-flex items-center gap-1">
            <VerdictBadge verdict={v} />
            <span className="text-xs font-bold text-slate-600">×{n}</span>
          </span>
        ))}
      </div>

      {/* Detailed table */}
      {nonOk.length === 0 ? (
        <div className="p-6 text-center text-emerald-800 bg-emerald-50">
          <CheckCircle2 className="inline me-2" size={18} />
          كل الدفعات تحمل قيود سليمة ومطابقة للـ Cash Registry.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase text-slate-600">
                <th className="px-3 py-2">Payment</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Payment&apos;s Bank</th>
                <th className="px-3 py-2">Bank → GL Link</th>
                <th className="px-3 py-2">Journal Cash-Side Account</th>
                <th className="px-3 py-2">Match?</th>
                <th className="px-3 py-2">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {nonOk.map((r, i) => (
                <React.Fragment key={i}>
                  <tr className="border-t border-slate-100 align-top">
                    <td className="px-3 py-2">
                      <div className="font-mono text-xs font-bold">{r.payment_no}</div>
                      <div className="text-[11px] text-slate-500">{r.payment_date} · {r.payment_type}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(r.amount)}</td>
                    <td className="px-3 py-2">
                      {r.payment_bank_account_name ? (
                        <div className="text-xs">{r.payment_bank_account_name}</div>
                      ) : <span className="text-rose-600 text-xs">— لا يوجد —</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.payment_bank_link_account_name ? (
                        <>
                          <div>{r.payment_bank_link_account_name}</div>
                          <div className="font-mono text-[10px] text-slate-500">
                            {r.payment_bank_link_account_number}
                          </div>
                        </>
                      ) : <span className="text-rose-600">— بلا ربط —</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.journal_cash_side_account_name ? (
                        <>
                          <div>{r.journal_cash_side_account_name}</div>
                          <div className="font-mono text-[10px] text-slate-500">
                            {r.journal_cash_side_account_number}
                            {' · '}Cr {fmt(r.journal_cash_side_credit)}
                            {' Dr '}{fmt(r.journal_cash_side_debit)}
                          </div>
                        </>
                      ) : <span className="text-rose-600">— لا يوجد —</span>}
                    </td>
                    <td className="px-3 py-2">
                      {r.matches_bank_link === true
                        ? <span className="text-emerald-700 font-bold text-xs">✓</span>
                        : r.matches_bank_link === false
                          ? <span className="text-rose-700 font-bold text-xs">✕</span>
                          : <span className="text-slate-500 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <VerdictBadge verdict={r.verdict} />
                    </td>
                  </tr>
                  <tr className="border-b border-slate-200 bg-slate-50/60">
                    <td colSpan={7} className="px-4 py-2 text-xs">
                      <div className="text-slate-800">
                        <b className="text-indigo-700">التفسير:</b> {r.explanation}
                      </div>
                      {r.suggested_fix && (
                        <div className="text-slate-800 mt-1">
                          <b className="text-emerald-700">الإصلاح المقترح:</b> {r.suggested_fix}
                        </div>
                      )}
                      {r.other_bank_owns_account && (
                        <div className="text-amber-800 mt-1">
                          <b>ملاحظة:</b> هذا الحساب مربوط ببنك آخر ({r.other_bank_owns_account.name}).
                        </div>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
